import {  Refresh } from '@mui/icons-material';
import { MenuItem, Select, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import SearchBar, { searchFunction } from '../../elements/SearchBar';
import TableComponent from '../../elements/TableComponent';
import { TestPlanTableHeaders } from '../test-case/testCaseTableHead';

const TestCaseSelectionTable = ({
  setSelectedTestCases,
  selectedTestCases,
  data,
  loading,
  moduleOptions,
  fetchTestCases,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedModule, setSelectedModule] = useState('All');
  const [automatedFilter, setAutomatedFilter] = useState('All');

  const formatData = () => {
    return data?.map((testcase) => ({
      ...testcase,
      module:
        moduleOptions?.find((module) => module?.id === testcase.module)?.name ||
        '',
    }));
  };

  useEffect(() => {
    const formattedData = formatData();
    applyFilters(formattedData);
  }, [data, moduleOptions, selectedModule, searchValue, automatedFilter]);

  const applyFilters = (formattedData) => {
    let filtered = filterByModule(formattedData, selectedModule);

    if (searchValue) {
      filtered = searchFunction(filtered, ['key', 'summary'], searchValue);
    }

    if (automatedFilter === 'Automated Only') {
      filtered = filtered.filter((item) => item.is_automated === 'Yes');
    }

    setFilteredData(filtered);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const filterByModule = (data, module) => {
    if (module === 'All') return data;
    return data.filter((item) => item.module === module);
  };

  const handleModuleChange = (module) => {
    setSelectedModule(module);
  };

  const handleAutomatedFilterChange = (event) => {
    setAutomatedFilter(event.target.value);
  };

  const calculateInsights = (data) => {
    const total = data?.length || 0;
    const automatedCount = data?.filter(
      (testcase) => testcase.is_automated === 'Yes'
    ).length;
    const percentage = total > 0 ? (automatedCount / total) * 100 : 0;
    return percentage;
  };

  return (
    <div>
      {loading ? (
        <Typography className="text-center text-muted p-5">
          Generating test plan...
        </Typography>
      ) : (
        <>
          <Typography className="mb-3 text-muted">
            Note: To create a test cycle, select the desired test cases by
            clicking the checkboxes next to them, then click the{' '}
            <strong>Create Test Cycle</strong> button below.
          </Typography>
          <Row className="mb-4">
            <Col>
              <div className="p-2 px-3 border rounded">
                <Typography
                  sx={{ fontSize: '16px' }}
                  className="text-primary mb-2">
                  Test Plan Suggestion Insights
                </Typography>
                <div className="d-flex align-items-center">
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: '16px' }}
                    className="me-4">
                    {/* <span><Circle fontSize='8px' color='red' /></span> */}
                    Total Test Cases:{' '}
                    <span className="text-success">{data?.length}</span>
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontSize: '16px' }}>
                    Automation Coverage:{' '}
                    <span className="text-success">
                      {Math.round(calculateInsights(data))}%
                    </span>
                  </Typography>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="align-items-center mb-3">
            <Col className="d-flex">
              <SearchBar
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                clearSearch={() => handleSearchChange('')}
                placeholder="Search Test Cases by Key or Description"
              />
            </Col>
            <Col md={2}>
              <Select
                value={selectedModule}
                onChange={(e) => handleModuleChange(e.target.value)}
                displayEmpty
                sx={{
                  '.MuiSelect-select': {
                    padding: '13px',
                  },
                }}
                fullWidth>
                <MenuItem value="All">All Modules</MenuItem>
                {moduleOptions.map((module) => (
                  <MenuItem key={module.id} value={module.name}>
                    {module.name}
                  </MenuItem>
                ))}
              </Select>
            </Col>
            <Col md={2}>
              <Select
                value={automatedFilter}
                onChange={handleAutomatedFilterChange}
                displayEmpty
                sx={{
                  '.MuiSelect-select': {
                    padding: '13px',
                  },
                }}
                fullWidth>
                <MenuItem value="All">All Test Cases</MenuItem>
                <MenuItem value="Automated Only">Automated Only</MenuItem>
              </Select>
            </Col>
            <Col md="auto" className="d-flex justify-content-end">
              <Button
                variant="outline-secondary"
                className="remove-focus me-2"
                onClick={fetchTestCases}
                title="Click to refresh">
                <Refresh color={'#6c757d'} size={'20px'} />
              </Button>
            </Col>
          </Row>

          <TableComponent
            data={filteredData.map((testCase) => ({
              ...testCase,
              isRowDisabled: testCase?.status?.name !== 'Approved',
            }))}
            headerTabs={TestPlanTableHeaders}
            isLoading={loading}
            selected={selectedTestCases.map((testCase) => testCase.id)}
            setSelected={setSelectedTestCases}
            setSelectedRows={setSelectedTestCases}
            showPages={false}
            striped
            showCheckbox={true}
            checkboxDisabledTitle="Please approve test case to add in the test cycle"
          />
        </>
      )}
    </div>
  );
};

export default TestCaseSelectionTable;
